import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Printer, Loader2, AlertCircle, Package, Filter } from 'lucide-react'
import { apiGet } from '../lib/api'

type GroupBy = 'category' | 'order' | 'sucursal'

interface PickItem {
  orderId: string
  orderNumber: string
  itemId: string
  productId: string | null
  sku: string
  name: string
  quantity: number
  categoryId: string | null
  categoryName: string | null
  location: string | null
  shippingState: string
  shippingCity: string
  customerName: string
}

interface PickGroup {
  key: string
  label: string
  totalItems: number
  totalUnits: number
  items: PickItem[]
}

interface PickResponse {
  status: string
  groupBy: GroupBy
  date: string | null
  totals: { orders: number; items: number; units: number }
  groups: PickGroup[]
}

const STATUS_OPTIONS = [
  { value: 'paid', label: 'Pagados' },
  { value: 'preparing', label: 'Preparando' },
  { value: 'shipped', label: 'Enviados' },
]

const GROUP_BY_OPTIONS: Array<{ value: GroupBy; label: string; help: string }> = [
  {
    value: 'category',
    label: 'Por categoría',
    help: 'Agrupa los items de todos los pedidos por categoría — útil para recolectar todos los pads de freno juntos.',
  },
  {
    value: 'order',
    label: 'Por pedido',
    help: 'Una sección por pedido con sus items.',
  },
  {
    value: 'sucursal',
    label: 'Por sucursal',
    help: 'Agrupa por estado de envío.',
  },
]

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function PickingList() {
  const [status, setStatus] = useState<string>('preparing')
  const [groupBy, setGroupBy] = useState<GroupBy>('category')
  const [date, setDate] = useState<string>('')

  const queryString = new URLSearchParams({
    status,
    groupBy,
    ...(date ? { date } : {}),
  }).toString()

  const { data, isLoading, isError, refetch, isFetching } = useQuery<PickResponse>({
    queryKey: ['picking-list', status, groupBy, date],
    queryFn: () => apiGet<PickResponse>(`/api/admin/picking-list?${queryString}`),
  })

  const groupHelp = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.help || ''

  return (
    <>
      {/* Estilos print: oculta filtros y todo lo que no sea la lista */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .picking-print-hide { display: none !important; }
          .picking-print-area { padding: 0 !important; margin: 0 !important; }
          .picking-group {
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #000 !important;
            margin-bottom: 16pt !important;
          }
          .picking-group h2 { font-size: 16pt !important; }
          .picking-table th, .picking-table td {
            padding: 4pt 6pt !important;
            font-size: 10pt !important;
            border-bottom: 1px solid #ccc !important;
          }
          .picking-table thead th {
            background: #eee !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page { margin: 12mm; size: A4; }
        }
      `}</style>

      <div className="picking-print-hide">
        <Topbar
          title="Picking list"
          subtitle="Hoja de recolección para almacén"
          actions={
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Printer className="size-3.5" /> Imprimir
            </button>
          }
        />
      </div>

      <div className="picking-print-area space-y-4 p-6">
        {/* Filtros */}
        <section className="picking-print-hide rounded-2xl border border-ink-200 bg-white p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">Estado</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">Agrupar por</span>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              >
                {GROUP_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">Fecha (opcional)</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayISO()}
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>

            {date && (
              <button
                type="button"
                onClick={() => setDate('')}
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Limpiar fecha
              </button>
            )}

            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
            >
              {isFetching ? <Loader2 className="size-3.5 animate-spin" /> : <Filter className="size-3.5" />}
              Actualizar
            </button>
          </div>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-500">
            <Package className="mt-0.5 size-3.5 shrink-0 text-ink-400" />
            {groupHelp}
          </p>
        </section>

        {/* Header impresión */}
        <header className="hidden print:block">
          <h1 style={{ fontSize: '20pt', margin: 0, fontWeight: 800 }}>
            Picking list — Maz Autopartes
          </h1>
          <p style={{ fontSize: '10pt', marginTop: 4 }}>
            Estado: <strong>{status}</strong> · Agrupado por <strong>{groupBy}</strong>
            {date ? ` · Fecha ${date}` : ''} · Generado{' '}
            {new Date().toLocaleString('es-MX', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </p>
        </header>

        {/* Totales */}
        {data && (
          <div className="rounded-2xl border border-ink-200 bg-white p-4 print:border-0 print:p-0">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Pedidos</div>
                <div className="text-2xl font-bold text-ink-900">{data.totals.orders}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Líneas</div>
                <div className="text-2xl font-bold text-ink-900">{data.totals.items}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Unidades</div>
                <div className="text-2xl font-bold text-ink-900">{data.totals.units}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500">Grupos</div>
                <div className="text-2xl font-bold text-ink-900">{data.groups.length}</div>
              </div>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="grid place-items-center p-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando picking list…</p>
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="size-4" />
            Error consultando picking list.
          </div>
        )}

        {data && data.groups.length === 0 && (
          <div className="rounded-2xl border border-dashed border-ink-300 bg-white p-10 text-center text-sm text-ink-500">
            No hay pedidos en estado <strong>{status}</strong>
            {date ? ` para ${date}` : ''}.
          </div>
        )}

        {/* Grupos */}
        {data &&
          data.groups.map((g) => (
            <section
              key={g.key}
              className="picking-group rounded-2xl border border-ink-200 bg-white shadow-sm"
            >
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-5 py-3">
                <h2 className="text-base font-bold text-ink-900">{g.label}</h2>
                <div className="flex items-center gap-3 text-xs text-ink-600">
                  <span>
                    <strong>{g.totalItems}</strong> líneas
                  </span>
                  <span>·</span>
                  <span>
                    <strong>{g.totalUnits}</strong> unidades
                  </span>
                </div>
              </header>
              <div className="overflow-x-auto">
                <table className="picking-table w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-2 font-medium" style={{ width: 110 }}>
                        SKU
                      </th>
                      <th className="px-4 py-2 font-medium">Producto</th>
                      <th className="px-4 py-2 font-medium" style={{ width: 120 }}>
                        Ubicación
                      </th>
                      <th
                        className="px-4 py-2 text-right font-medium"
                        style={{ width: 70 }}
                      >
                        Cant.
                      </th>
                      <th className="px-4 py-2 font-medium" style={{ width: 130 }}>
                        Pedido
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={it.itemId} className="border-t border-ink-100">
                        <td className="px-4 py-2 font-mono text-xs text-ink-700">{it.sku}</td>
                        <td className="px-4 py-2 text-ink-900">{it.name}</td>
                        <td className="px-4 py-2 text-ink-700">
                          {it.location || <span className="text-ink-400">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right text-base font-bold text-ink-900">
                          {it.quantity}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-ink-700">
                          #{it.orderNumber}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
      </div>
    </>
  )
}

export default PickingList
